import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Alert,
  Container,
  Row,
  Col,
  Form,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import Footer from "../../components/layout/Footer";
import logo from "../../assets/img/logo.png";
import { addCommasToNumber } from "../../utils/AboutNumber";

// 수량별 권장판매가 컬럼
const PRICE_COLUMNS = [
  { key: "retail_price_over1", label: "1개" },
  { key: "retail_price_over2", label: "2개" },
  { key: "retail_price_over3", label: "3개" },
  { key: "retail_price_over4", label: "4개" },
  { key: "retail_price_over5", label: "5개" },
  { key: "retail_price_over6", label: "6개 이상" },
];

const formatPrice = (value) => {
  const num = Number(value);
  return num > 0 ? addCommasToNumber(num) : "-";
};

// 판매가 표시 예시 카드 (허용 / 불가)
const EXAMPLE_PRODUCT_NAME =
  "피터앤존 빌베리 60000mg + 루테인25mg 60캡슐 [원산지:뉴질랜드]";

function PriceExampleCard({ allowed }) {
  const badgeColor = allowed ? "#198754" : "#dc3545";

  return (
    <div
      className="h-100 p-3 bg-white"
      style={{ border: `2px solid ${badgeColor}`, borderRadius: "10px" }}
    >
      <div className="fw-bold mb-3" style={{ color: badgeColor }}>
        {allowed ? "○ 쿠폰적용 방식 (허용)" : "✕ 할인가격 노출 방식 (불가)"}
      </div>
      <div className="p-3 border rounded">
        <div className="mb-2">{EXAMPLE_PRODUCT_NAME}</div>
        {allowed ? (
          <div className="d-flex justify-content-between align-items-end">
            <div>
              <div className="fw-bold">57,000원</div>
              <div style={{ color: "#e4002b" }}>
                <span className="fs-4 fw-bold">50,160</span>원 최대할인가
              </div>
            </div>
            <span
              className="px-2 py-1 rounded"
              style={{
                color: "#e4002b",
                background: "#fdecee",
                border: "1px solid #f5b5bf",
                fontSize: "0.9rem",
              }}
            >
              쿠폰 받기
            </span>
          </div>
        ) : (
          <div>
            <div className="text-muted text-decoration-line-through">
              57,000원
            </div>
            <div>
              <span className="fw-bold me-1">10%</span>
              <span style={{ color: "#e4002b" }}>
                <span className="fs-4 fw-bold">51,300</span>원
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 small text-muted">
        {allowed
          ? "상세 페이지에는 정가를 표시하고, 쿠폰 등 고객 개별 혜택으로 할인 적용"
          : "상세 페이지에 정가 대비 할인된 가격을 직접 노출"}
      </div>
    </div>
  );
}

function SalesPrice() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 검색 조건
  const [keyword, setKeyword] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceColumn, setPriceColumn] = useState("retail_price_over1");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  useEffect(() => {
    const getSalesPrice = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_NOAUTH_API_URL}/public/sales-price`,
        );
        if (res.data.result === "success") {
          setProducts(res.data.data);
        } else {
          setError("판매가 정보를 불러오지 못했습니다.");
        }
      } catch (err) {
        console.error("데이터를 불러오는데 실패했습니다", err);
        setError("판매가 정보를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    getSalesPrice();
  }, []);

  // 브랜드별 제품 수
  const brandCounts = useMemo(() => {
    const counts = {};
    products.forEach((p) => {
      if (p.brand_name) counts[p.brand_name] = (counts[p.brand_name] || 0) + 1;
    });
    return counts;
  }, [products]);
  // 브랜드 버튼은 알파벳 역순으로 나열
  const brands = Object.keys(brandCounts).sort((a, b) =>
    b.localeCompare(a, "en", { sensitivity: "base" }),
  );

  // 브랜드 클릭 시 선택/해제 (복수 선택 가능)
  const toggleBrand = (b) => {
    setSelectedBrands((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b],
    );
  };

  const filtered = useMemo(() => {
    // 검색어를 공백 기준으로 나눠, 모든 키워드가 일치하는 제품만 검색 (AND 조건)
    const keywords = keyword.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const min = minPrice === "" ? null : Number(minPrice);
    const max = maxPrice === "" ? null : Number(maxPrice);

    const matched = [];
    products.forEach((p) => {
      if (selectedBrands.length > 0 && !selectedBrands.includes(p.brand_name))
        return;

      if (keywords.length > 0) {
        const target = [p.en_name, p.brand_name, p.barcode, p.product_code]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!keywords.every((k) => target.includes(k))) return;
      }

      // 금액범위 지정 시 해당 수량 판매가가 없는(0) 제품은 제외
      const price = Number(p[priceColumn]);
      if ((min !== null || max !== null) && !(price > 0)) return;
      if (min !== null && !(price >= min)) return;
      if (max !== null && !(price <= max)) return;

      matched.push(p);
    });

    return matched;
  }, [products, keyword, selectedBrands, priceColumn, minPrice, maxPrice]);

  const handleReset = () => {
    setKeyword("");
    setSelectedBrands([]);
    setPriceColumn("retail_price_over1");
    setMinPrice("");
    setMaxPrice("");
  };

  return (
    <>
      <div className="p-3" style={{ background: "#343a40" }}>
        <img src={logo} alt="로고" width="120px" />
      </div>

      <Container fluid className="px-4">
        <Row className="my-4">
          <Col>
            <h3>권장판매가 조회</h3>
          </Col>
        </Row>

        <Alert color="warning" className="text-start">
          <h5 className="fw-bold">[중요 안내] 판매가 표시 방식</h5>
          <p className="mb-2">
            시장의 형평성을 유지하고 브랜드 가치를 보호하기 위해, 불특정
            다수에게 노출되는 제품 판매 화면(상세 페이지 등)에서는{" "}
            <b>정가 표시를 유지</b>해 주시길 부탁드립니다.
          </p>
          <p className="mb-2">
            다만, 고객 개인의 최종 결제 단계(장바구니, 쿠폰 적용 등)에서
            개별적으로 혜택을 드리는 방식은 관여하지 않으니, 이 점 참고하시어
            판매 화면 수정을 부탁드리겠습니다.
          </p>
          <p className="mb-3">
            사장님의 너그러운 양해와 적극적인 협조 부탁드립니다.
          </p>
          <Row className="g-3">
            <Col md={6}>
              <PriceExampleCard allowed={false} />
            </Col>
            <Col md={6}>
              <PriceExampleCard allowed />
            </Col>
          </Row>
        </Alert>

        <div className="my-4 p-3 border rounded bg-light">
          <Form onSubmit={(e) => e.preventDefault()}>
            <Row className="g-3 align-items-end">
              <Col md={6}>
                <div>
                  <Label for="keyword">검색어</Label>
                  <Input
                    type="text"
                    id="keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="제품명, 브랜드, 바코드, 제품코드"
                  />
                </div>
              </Col>
              <Col md={5}>
                <div>
                  <Label for="minPrice">금액범위</Label>
                  <InputGroup>
                    <Input
                      type="select"
                      value={priceColumn}
                      onChange={(e) => setPriceColumn(e.target.value)}
                      style={{ maxWidth: "120px" }}
                    >
                      {PRICE_COLUMNS.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </Input>
                    <Input
                      type="number"
                      id="minPrice"
                      min="0"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="최소"
                    />
                    <InputGroupText>~</InputGroupText>
                    <Input
                      type="number"
                      min="0"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="최대"
                    />
                  </InputGroup>
                </div>
              </Col>
              <Col md={1}>
                <Button color="secondary" outline block onClick={handleReset}>
                  초기화
                </Button>
              </Col>
            </Row>
          </Form>

          <div className="mt-3">
            <Label className="d-block">브랜드</Label>
            <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                color="primary"
                outline={selectedBrands.length > 0}
                onClick={() => setSelectedBrands([])}
              >
                전체 ({addCommasToNumber(products.length)})
              </Button>
              {brands.map((b) => (
                <Button
                  key={b}
                  size="sm"
                  color="primary"
                  outline={!selectedBrands.includes(b)}
                  onClick={() => toggleBrand(b)}
                >
                  {b} ({addCommasToNumber(brandCounts[b])})
                </Button>
              ))}
            </div>
          </div>
        </div>

        <Alert color="info" className="text-start">
          <ul className="mb-0 ps-4">
            <li>
              검색어를 띄어쓰기로 여러 개 입력하면 모든 검색어가 포함된 제품만
              표시됩니다.
            </li>
            <li>
              브랜드를 클릭하면 해당 브랜드 제품만 표시됩니다. (복수 선택 가능)
            </li>
            <li>표시된 금액은 구매 수량별 권장판매가(합계)입니다.</li>
            <li>'-'로 표시된 수량은 권장판매가가 지정되지 않은 수량입니다.</li>
            <li>
              금액범위는 선택한 수량(1개 ~ 6개 이상)의 판매가를 기준으로
              적용됩니다.
            </li>
          </ul>
        </Alert>

        {loading && (
          <div className="text-center p-5">
            <Spinner color="primary" />
          </div>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        {!loading && !error && (
          <>
            <div className="mb-2 text-end">
              검색결과 <b>{addCommasToNumber(filtered.length)}</b>건
            </div>
            <Table
              striped
              bordered
              hover
              responsive
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "960px",
              }}
            >
              <colgroup>
                <col style={{ width: "50px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "90px" }} />
                <col />
                {PRICE_COLUMNS.map((c) => (
                  <col key={c.key} style={{ width: "100px" }} />
                ))}
              </colgroup>
              <thead>
                <tr className="table-primary">
                  <th className="text-center">No</th>
                  <th className="text-center">브랜드</th>
                  <th className="text-center">제품코드</th>
                  <th className="text-center">제품명</th>
                  {PRICE_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className="text-center"
                      style={{
                        fontWeight: c.key === priceColumn ? "700" : undefined,
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4 + PRICE_COLUMNS.length} className="p-5">
                      검색결과가 없습니다.
                    </td>
                  </tr>
                )}
                {filtered.map((p, idx) => (
                  <tr key={p.product_code}>
                    <td className="text-center">{idx + 1}</td>
                    <td
                      className="text-center"
                      style={{ wordBreak: "break-word" }}
                    >
                      {p.brand_name}
                    </td>
                    <td className="text-center">{p.product_code}</td>
                    <td style={{ textAlign: "left", wordBreak: "break-word" }}>
                      {p.en_name}
                    </td>
                    {PRICE_COLUMNS.map((c) => (
                      <td key={c.key} className="text-end">
                        {formatPrice(p[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Container>
      <Footer />
    </>
  );
}

export default SalesPrice;
